/**
 * Weighted pipeline computation for the Operations Kanban.
 *
 * "Weighted pipeline" = for every ACTIVE fulfillment stage, sum the value of
 * the cards sitting in that stage and multiply by the stage's completion
 * weight (`valuePercent`):
 *
 *   weighted = Σ_stage ( Σ_card amount ) × valuePercent / 100
 *
 * The "Done" stage (100%) is excluded — it is completed work, not work in
 * progress. The board data this derives from is already windowed server-side
 * by quote CREATION date (see dao/quotes-v4.js getKanban), so no date
 * filtering happens here: whatever the board shows is what gets weighted.
 */

import type { ProjectsKanbanData } from './types';

/** Stages excluded from the "in progress" pipeline. */
export const EXCLUDED_STAGES = new Set<string>(['done']);

export interface WeightedStageBreakdown {
  stage: string;
  label: string;
  valuePercent: number;
  /** Raw (unweighted) summed amount of the cards in this stage. */
  rawValue: number;
  /** rawValue × valuePercent / 100. */
  weightedValue: number;
  count: number;
}

export interface WeightedPipelineResult {
  /** Grand weighted total across all active stages. */
  total: number;
  /** Grand raw (unweighted) total across all active stages. */
  rawTotal: number;
  /** Number of cards counted. */
  count: number;
  currency: string;
  byStage: WeightedStageBreakdown[];
}

export function computeWeightedPipeline(data?: ProjectsKanbanData): WeightedPipelineResult {
  const byStage: WeightedStageBreakdown[] = [];
  let total = 0;
  let rawTotal = 0;
  let count = 0;
  let currency = 'CAD';

  for (const col of data?.columns ?? []) {
    if (EXCLUDED_STAGES.has(col.stage)) continue;

    let rawValue = 0;
    let stageCount = 0;
    for (const project of col.projects) {
      rawValue += project.amount || 0;
      stageCount += 1;
      if (project.currency) currency = project.currency;
    }

    const weightedValue = rawValue * (col.valuePercent / 100);
    total += weightedValue;
    rawTotal += rawValue;
    count += stageCount;

    byStage.push({
      stage: col.stage,
      label: col.label,
      valuePercent: col.valuePercent,
      rawValue,
      weightedValue,
      count: stageCount,
    });
  }

  return { total, rawTotal, count, currency, byStage };
}
